import { useEffect, useState } from "react";
import { Stack, CircularProgress, Button, Typography, Input, List, ListItem, useTheme } from "@mui/material";
import { FormProvider, useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { WhiteBox } from "@foodpilot/foods";
import { Link, useParams } from "react-router";
import { useMutation } from "react-query";
import { FormInput } from "src/components/FormInput";
import { resetPasswordFn } from "src/api/authApi";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import ArrowLeft from "src/assets/icons/arrow-left.svg?react";
import CheckIcon from "src/assets/icons/check.svg?react";
import { t } from "i18next";

export type ResetPasswordInput = {
    password: string;
    confirmPassword: string;
    token: string;
};

export const ResetPassword = () => {
    const { token } = useParams();
    const { t } = useTranslation();
    const [error, setError] = useState(false);
    const [passwordChanged, setPasswordChanged] = useState<boolean>(false);
    const [passwordErrors, setPasswordErrors] = useState<Array<{ message: string }> | null>(null);

    const validationMessages = getResetPasswordValidationMessages();
    const resetPasswordSchema = getResetPasswordSchema();
    const passwordFieldSchema = getPasswordFieldSchema();

    const methods = useForm<ResetPasswordInput>({
        resolver: zodResolver(resetPasswordSchema),
        defaultValues: {
            token: token ?? "",
            password: "",
            confirmPassword: "",
        },
    });

    //  API Login Mutation
    const { mutate: resetPassword, isLoading } = useMutation(
        (userData: ResetPasswordInput) => resetPasswordFn(userData),
        {
            onSuccess: (response) => {
                if (response.status !== 200) {
                    throw new Error(response.statusText);
                }
                setPasswordChanged(true);
            },
            onError: () => {
                setError(true);
            },
        },
    );

    useEffect(() => {
        methods.reset();
    }, [methods.formState.isSubmitSuccessful]);

    if (passwordChanged && !error) {
        return <ResetPasswordSuccess />;
    }

    return (
        <FormProvider {...methods}>
            <WhiteBox
                padding="40px"
                paddingTop="32px"
                boxShadow="0 2px 8px 0 rgba(42, 43, 39, 0.04)"
                position="relative"
            >
                {isLoading && (
                    <Stack
                        sx={{
                            background: "rgba(255, 255, 255, 0.5)",
                            position: "absolute",
                            inset: "calc(40px / 2)",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1,
                        }}
                    >
                        <CircularProgress />
                    </Stack>
                )}
                <Stack
                    component="form"
                    onSubmit={methods.handleSubmit((values) => {
                        setError(false);
                        values.token = encodeURIComponent(values.token);
                        resetPassword(values);
                    })}
                    noValidate
                    autoComplete="off"
                    gap="36px"
                >
                    <Typography variant="h2" align="center">
                        {t("reset_password_title")}
                    </Typography>

                    {error && (
                        <Typography variant="h4" align="center" color="error">
                            {t("reset_failed")}
                        </Typography>
                    )}

                    <Stack gap="24px">
                        <Stack gap="20px">
                            <FormInput
                                type="password"
                                name="password"
                                label={t("reset_password_pwd")}
                                inputProps={{ maxLength: 32 }}
                                onKeyUp={(e) => {
                                    setPasswordErrors([]);

                                    try {
                                        passwordFieldSchema.parse((e.target as HTMLInputElement).value);
                                    } catch (e: unknown) {
                                        if (e instanceof Error && e.name === "ZodError") {
                                            setPasswordErrors(JSON.parse(e.message));
                                        }
                                    }
                                }}
                            />

                            <PasswordChecksList checks={Object.values(validationMessages)} errors={passwordErrors} />
                        </Stack>

                        <FormInput
                            type="password"
                            name="confirmPassword"
                            label={t("reset_password_pwd_confirmation")}
                            inputProps={{ maxLength: 32 }}
                        />

                        <Controller
                            defaultValue={token}
                            control={methods.control}
                            name="token"
                            render={({ field }) => <Input {...field} type="hidden" sx={{ display: "none" }} />}
                        />

                        <Button variant="primary" type="submit" disabled={!methods.formState.isValid}>
                            {t("reinitialiser_votre_mot_de_passe")}
                        </Button>

                        <Button
                            variant="text"
                            component={Link}
                            to="/login"
                            startIcon={<ArrowLeft />}
                            sx={{ width: "100%", height: "auto" }}
                        >
                            {t("revenir_a_la_connexion")}
                        </Button>
                    </Stack>
                </Stack>
            </WhiteBox>
        </FormProvider>
    );
};

type PasswordChecksListProps = {
    checks: string[];
    errors: Array<{ message: string }> | null;
};
const PasswordChecksList = (props: PasswordChecksListProps) => {
    const theme = useTheme();
    const { checks, errors } = props;

    return (
        <List
            sx={{
                color: theme.custom.grey[2000],
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                padding: 0,
                listStyleType: "disc",
                listStylePosition: "inside",
                textAlign: "center",
                ">li": {
                    minHeight: "22px",
                    display: "list-item",
                    listStyleType: "disc",
                    padding: 0,
                    "&::marker": {
                        color: "inherit",
                    },
                    "&.satisfied": {
                        color: theme.custom.green[800],
                        position: "relative",
                        "&::marker": {
                            color: "transparent",
                        },
                    },
                },
            }}
        >
            {checks.map((message, index) => {
                const satisfied =
                    errors &&
                    !errors.find((e: { message: string }) => {
                        return e.message === message;
                    });

                return (
                    <ListItem key={index} className={satisfied ? "satisfied" : ""}>
                        {satisfied && (
                            <CheckIcon
                                width="20px"
                                height="20px"
                                style={{
                                    position: "absolute",
                                    left: "-4px",
                                    top: "2px",
                                }}
                            />
                        )}
                        <Typography variant="h5" fontWeight={500} color="inherit" display="inline">
                            {message}
                        </Typography>
                    </ListItem>
                );
            })}
        </List>
    );
};

const ResetPasswordSuccess = () => {
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <WhiteBox padding="40px" boxShadow="0 2px 8px 0 rgba(42, 43, 39, 0.04)">
            <Stack gap="32px">
                <Stack gap="16px" alignItems="center">
                    <Stack
                        width="56px"
                        height="56px"
                        borderRadius="100%"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            backgroundColor: theme.palette.primary[50],
                        }}
                    >
                        <CheckIcon width="28px" />
                    </Stack>
                    <Typography variant="h2" align="center">
                        {t("mot_de_passe_reinitialise")}
                    </Typography>
                    <Typography variant="body" align="center" sx={{ color: theme.custom.grey[2000] }}>
                        {t("vous_pouvez_a_nouveau_vous_connectez_a_votre_plateforme")}
                    </Typography>
                </Stack>

                <Button variant="primary" component={Link} to="/login" startIcon={<ArrowLeft />}>
                    {t("revenir_a_l_ecran_de_connexion")}
                </Button>
            </Stack>
        </WhiteBox>
    );
};

const getResetPasswordValidationMessages = () => {
    return {
        min: t("reset_password.validation.more_characters", {
            defaultValue: "More than {{count}} characters",
            count: 8,
        }),
        atLeastOneSpecialCharacter: t("reset_password.validation.at_least_one_special_character", {
            defaultValue: "At least one special character: {{characters}}",
            characters: "!@#$*€%£",
        }),
        atLeastOneCapitalLetter: t(
            "reset_password.validation.at_least_one_capital_letter",
            "At least one capital letter",
        ),
        atLeastOneDigit: t("reset_password.validation.at_least_one_digit", "At least one digit"),
    };
};

const getPasswordFieldSchema = () => {
    const messages = getResetPasswordValidationMessages();

    return z
        .string()
        .min(8, messages.min)
        .regex(new RegExp(/@|!|#|\$|\*|€|%|£/), { message: messages.atLeastOneSpecialCharacter })
        .regex(new RegExp(/[A-Z]/), { message: messages.atLeastOneCapitalLetter })
        .regex(new RegExp(/[0-9]/), { message: messages.atLeastOneDigit });
};

const getResetPasswordSchema = () => {
    const passwordFieldSchema = getPasswordFieldSchema();

    return z
        .object({
            password: passwordFieldSchema,
            confirmPassword: z.string().min(1, t("reset_password.validation.required", "The password is required")),
            token: z.string(),
        })
        .refine(
            (args) => {
                return args.password === args.confirmPassword;
            },
            {
                path: ["confirmPassword"],
                message: t("reset_password.validation.doesnt_match", "The passwords don't match"),
            },
        );
};
